/**
 * Game theater chrome (close / icon / actions) placement.
 *
 * Default is a side rail so the playfield can use the full height. Portrait
 * phones are the exception: they keep a bottom bar. Flip
 * `GAME_THEATER_CHROME_SIDE` to `"right"` to try the opposite rail.
 */

import { GAME_THEATER_PLAYER_BG_CLASS } from "@/lib/constants/layout";

export const GAME_THEATER_CHROME_SIDES = ["left", "right"] as const;
export type GameTheaterChromeSide = (typeof GAME_THEATER_CHROME_SIDES)[number];

/** Side rail used except on portrait mobile. */
export const GAME_THEATER_CHROME_SIDE: GameTheaterChromeSide = "left";

const STAGE_FLEX_DIRECTION_CLASS: Record<GameTheaterChromeSide, string> = {
  right: "flex-row",
  left: "flex-row-reverse",
};

/**
 * Portrait viewports below `md` (768px) are the only bottom-chrome case.
 * `!flex-col` wins over `flex-row` / `flex-row-reverse`.
 */
export const GAME_THEATER_PORTRAIT_MOBILE_BOTTOM_CLASS = "max-md:portrait:!flex-col";

export function getGameTheaterStageClass(
  side: GameTheaterChromeSide = GAME_THEATER_CHROME_SIDE
): string {
  return `flex h-full w-full min-h-0 shrink-0 overflow-visible ${STAGE_FLEX_DIRECTION_CLASS[side]} ${GAME_THEATER_PORTRAIT_MOBILE_BOTTOM_CLASS}`;
}

const CHROME_BAR_SIDE_CLASS: Record<GameTheaterChromeSide, string> = {
  right:
    "border-l border-white/10 pl-2 pr-[max(0.5rem,env(safe-area-inset-right))] max-md:portrait:!border-l-0 max-md:portrait:!border-t max-md:portrait:!pl-3 max-md:portrait:!pr-3",
  left: "border-r border-white/10 pr-2 pl-[max(0.5rem,env(safe-area-inset-left))] max-md:portrait:!border-r-0 max-md:portrait:!border-t max-md:portrait:!pl-3 max-md:portrait:!pr-3",
};

export function getGameTheaterChromeBarClass(
  side: GameTheaterChromeSide = GAME_THEATER_CHROME_SIDE
): string {
  return `relative flex h-full shrink-0 overflow-visible ${GAME_THEATER_PLAYER_BG_CLASS} px-0 py-3 max-md:portrait:!h-auto max-md:portrait:w-full max-md:portrait:pb-[max(0.75rem,env(safe-area-inset-bottom))] ${CHROME_BAR_SIDE_CLASS[side]}`;
}

/**
 * Pins an expanding rail button to the outer edge so hover labels grow inward
 * over the playfield. Flip follows `GAME_THEATER_CHROME_SIDE`.
 */
export function getGameTheaterRailExpandAnchorClass(
  side: GameTheaterChromeSide = GAME_THEATER_CHROME_SIDE
): string {
  return side === "right" ? "right-0" : "left-0";
}

/** Label sits toward the playfield; the icon stays on the outer edge. */
export function isGameTheaterRailLabelBeforeIcon(
  side: GameTheaterChromeSide = GAME_THEATER_CHROME_SIDE
): boolean {
  return side === "right";
}

/**
 * Spine title: `vertical-rl` is clockwise (top → bottom). Extra 180° on the
 * left rail makes it counterclockwise (bottom → top) so the baseline faces
 * the playfield on either side. Portrait mobile writes the name horizontally
 * in the bottom bar.
 */
export function getGameTheaterRailTitleClass(
  side: GameTheaterChromeSide = GAME_THEATER_CHROME_SIDE
): string {
  const spine =
    side === "left" ? "[writing-mode:vertical-rl] rotate-180" : "[writing-mode:vertical-rl]";
  return `${spine} max-md:portrait:![writing-mode:horizontal-tb] max-md:portrait:!rotate-0`;
}

const RAIL_TITLE_FIT_SLOP_PX = 1;

/** Portrait bottom-bar title slot uses Tailwind `px-2` (8px each side). */
export const THEATER_RAIL_TITLE_PORTRAIT_PADDING_X_PX = 8;

/**
 * Extra leftover height required on the side rail so a “fits” title cannot
 * clip a letter under the icon or action buttons.
 */
export const THEATER_RAIL_TITLE_SIDE_FIT_MARGIN_PX = 16;

export type TheaterRailTitleBox = {
  clientWidth: number;
  clientHeight: number;
  layout: "row" | "column";
};

/**
 * Slot for the theater title. Stretch to the rail width so glyph overflow
 * cannot shrink-wrap the slot thinner than the letters. When the label does
 * not fit on portrait, it leaves the flex flow so padding and `gap` are not
 * reserved beside the non-shrinking chrome controls.
 */
export function getTheaterRailTitleSlotClass(fits: boolean): string {
  const base =
    "flex min-h-0 min-w-0 w-full flex-1 items-center justify-center self-stretch overflow-hidden";
  if (fits) {
    return `${base} max-md:portrait:px-2`;
  }
  return `${base} max-md:portrait:pointer-events-none max-md:portrait:absolute max-md:portrait:left-0 max-md:portrait:top-0 max-md:portrait:h-0 max-md:portrait:w-0 max-md:portrait:overflow-hidden max-md:portrait:p-0`;
}

function isFlexRow(flexDirection: string): boolean {
  return flexDirection === "row" || flexDirection === "row-reverse";
}

/**
 * On the portrait bottom bar (flex row), leftover width is parent minus the
 * other chrome groups, the gaps that exist with the title present, and slot
 * padding. On the side rail (flex column), use leftover slot height and the
 * header width so a shrink-wrapped slot cannot fail the thickness check.
 */
export function getTheaterRailTitleAvailableBox(slot: HTMLElement): TheaterRailTitleBox {
  const parent = slot.parentElement;
  if (!parent) {
    return { clientWidth: slot.clientWidth, clientHeight: slot.clientHeight, layout: "column" };
  }

  const style = getComputedStyle(parent);
  const display = style.display;
  const isFlex = display === "flex" || display === "inline-flex";
  const direction = style.flexDirection;
  if (!isFlex || !isFlexRow(direction)) {
    return {
      clientWidth: Math.max(slot.clientWidth, parent.clientWidth),
      clientHeight: slot.clientHeight,
      layout: "column",
    };
  }

  const gap = Number.parseFloat(style.columnGap) || 0;
  let others = 0;
  for (const child of Array.from(parent.children)) {
    if (child === slot) continue;
    others += (child as HTMLElement).getBoundingClientRect().width;
  }

  const siblingCount = Math.max(0, parent.childElementCount - 1);
  const paddingX = THEATER_RAIL_TITLE_PORTRAIT_PADDING_X_PX * 2;
  const availableWidth = Math.max(0, parent.clientWidth - others - gap * siblingCount - paddingX);

  return {
    clientWidth: availableWidth,
    clientHeight: parent.clientHeight,
    layout: "row",
  };
}

/** True when the spine title’s full string fits in the rail slot. */
export function doesTheaterRailTitleFit(
  text: { scrollWidth: number; scrollHeight: number },
  slot: { clientWidth: number; clientHeight: number },
  extraLongPx = 0
): boolean {
  const textLong = Math.max(text.scrollWidth, text.scrollHeight);
  const textShort = Math.min(text.scrollWidth, text.scrollHeight);
  const slotLong = Math.max(slot.clientWidth, slot.clientHeight) - extraLongPx;
  const slotShort = Math.min(slot.clientWidth, slot.clientHeight);
  return (
    textLong <= slotLong + RAIL_TITLE_FIT_SLOP_PX && textShort <= slotShort + RAIL_TITLE_FIT_SLOP_PX
  );
}
